"use client";

import { useState } from "react";

type EditableImportCellProps = {
  /** The value as it is held (what the validation reads). */
  value: string;
  /** What is shown while not editing (the value, formatted). */
  displayValue: string;
  hasError: boolean;
  /** When the field only accepts some values, they are offered in a list. */
  options?: string[];
  onCommit: (value: string) => void;
};

/**
 * A cell of the validation table that is corrected in place: a click turns it into a
 * text field (or a list when the field accepts a fixed set of values). Enter or leaving
 * the field keeps the change, Escape drops it.
 */
export default function EditableImportCell({
  value,
  displayValue,
  hasError,
  options,
  onCommit,
}: EditableImportCellProps) {
  const [isEditing, setIsEditing] = useState(false);

  const finish = (next: string | null) => {
    setIsEditing(false);
    if (next !== null && next.trim() !== value.trim()) onCommit(next.trim());
  };

  if (isEditing && options) {
    // A value that is not in the list yet (the wrong one) stays selectable as it is.
    const listed = options.includes(value) || value === "" ? options : [value, ...options];
    return (
      <select
        autoFocus
        defaultValue={value}
        className="h-7 max-w-44 rounded border border-ac bg-white px-1 text-sm"
        onChange={(event) => finish(event.target.value)}
        onBlur={() => setIsEditing(false)}
        onKeyDown={(event) => {
          if (event.key === "Escape") setIsEditing(false);
        }}
      >
        <option value="">(vacío)</option>
        {listed.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }

  if (isEditing) {
    return (
      <input
        autoFocus
        defaultValue={value}
        className="h-7 w-40 rounded border border-ac bg-white px-1 text-sm"
        onBlur={(event) => finish(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") finish(event.currentTarget.value);
          if (event.key === "Escape") setIsEditing(false);
        }}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setIsEditing(true)}
      title={value.length > 30 ? `${value} (clic para editar)` : "Clic para editar"}
      className={`-mx-1 max-w-44 cursor-text truncate rounded px-1 text-left text-sm hover:bg-white hover:ring-1 hover:ring-ac ${
        hasError ? "text-red-600" : "text-fg"
      }`}
    >
      {displayValue || <span className="italic text-fg-muted">vacío</span>}
    </button>
  );
}
